import { z } from 'zod';

/**
 * NIVISION outgoing-webhook envelope (docs/nivision-webhook-api.md + real payloads in
 * apps/api/test/fixtures/nivision). The schema is ADDITIVE: unknown fields are tolerated (loose objects).
 * Only fields we actually use are typed. Never invent fields (golden rule 1).
 */
const loose = <T extends z.ZodRawShape>(shape: T) => z.looseObject(shape);

export const NivisionSignalSchema = loose({
  uuid: z.string(),
  severity: z.string(), // CRITICAL | HIGH | MEDIUM | LOW
  severityLabel: z.string().nullish(),
  fieldValue: z.unknown().optional(),
  ruleUuid: z.string(),
  ruleName: z.string(),
  ruleDescription: z.string().nullish(),
  fieldUuid: z.string().nullish(),
  fieldName: z.string().nullish(),
  createdAt: z.string().nullish(),
});
export type NivisionSignal = z.infer<typeof NivisionSignalSchema>;

export const NivisionFieldSchema = loose({
  uuid: z.string().optional(),
  key: z.string(),
  name: z.string().nullish(),
  label: z.string().nullish(),
  knownFieldId: z.string().nullish(),
  type: z.string().nullish(),
  value: z.unknown(),
});

export const NivisionEnvelopeSchema = loose({
  id: z.string(),
  type: z.string(),
  version: z.string().nullish(),
  timestamp: z.string().nullish(),
  tenant: loose({ uuid: z.string(), slug: z.string().nullish() }).nullish(),
  site: loose({ uuid: z.string(), slug: z.string().nullish() }).nullish(),
  call: loose({
    uuid: z.string(),
    status: z.string().nullish(),
    direction: z.string().nullish(),
    duration: z.number().nullish(),
    callerNumber: z.string().nullish(),
    receiverNumber: z.string().nullish(),
    extension: z.string().nullish(),
    startedAt: z.string(),
    endedAt: z.string().nullish(),
  }).nullish(),
  report: loose({
    uuid: z.string(),
    status: z.string().nullish(),
    reportUrl: z.string().nullish(),
    summary: z.string().nullish(),
    callScore: z.number().nullish(),
    agentScore: z.number().nullish(),
    callSentiment: z.string().nullish(),
    tags: z.array(z.string()).nullish(),
    outcome: loose({ uuid: z.string().nullish(), key: z.string().nullish(), label: z.string().nullish(), color: z.string().nullish() }).nullish(),
    outcomeReason: z.string().nullish(),
    isMeasured: z.boolean().nullish(),
    classifier: loose({ uuid: z.string().nullish(), name: z.string().nullish() }).nullish(),
    agent: loose({ uuid: z.string().nullish(), firstName: z.string().nullish(), lastName: z.string().nullish() }).nullish(),
  }).nullish(),
  agentRecognition: z.unknown().nullish(),
  fields: z.record(z.string(), NivisionFieldSchema).nullish(),
  signals: z.array(NivisionSignalSchema).nullish(),
  pipeline: loose({ callUuid: z.string().nullish(), reportUuid: z.string().nullish(), reportStatus: z.string().nullish() }).nullish(),
  delivery: loose({ partial: z.boolean().nullish(), stage: z.string().nullish() }).nullish(),
  metadata: z.unknown().nullish(),
});
export type NivisionEnvelope = z.infer<typeof NivisionEnvelopeSchema>;

export const SEVERITY_ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type Severity = (typeof SEVERITY_ORDER)[number];
export const severityRank = (s: string): number => SEVERITY_ORDER.indexOf(s.toUpperCase() as Severity);

/**
 * D-158 (data diet): a path inside the NIVISION envelope that a reader of the stored RawEvent body reads. '*' = every item of an
 * array; '?' (last segment) = only the presence of the node is read. Under `fields.<key>.value` the stored body keeps only the union
 * of the declared paths (apps/api/src/modules/nivision/digest/raw-allowlist.ts); a path that ends at a node keeps its whole subtree.
 */
export type RawPath = readonly string[];
